import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'react-toastify';

import { ListToolbar } from '~/components/layout';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  Pagination,
  type DataTableColumn,
  StatusChip,
} from '~/components/ui';
import { telemetryStatus } from '~/components/pages/ai/analytics/telemetryStatus';
import { LocalTime } from '~/components/common/LocalTime';
import { useTransientParam } from '~/hooks/useTransientParam';
import { useUrlParams } from '~/hooks/useUrlParams';
import { useDebouncedQuery } from '~/hooks/useDebouncedQuery';
import { useListUrlState } from '~/hooks/useListUrlState';
import {
  useGetAnalyticsTelemetryQuery,
  useLazyExportAnalyticsTelemetryQuery,
} from '~/modules/query/ai/analytics';
import type { AnalyticsTelemetrySortField, TelemetrySession } from '~/modules/query/ai/types';
import {
  TELEMETRY_STATUS_DESCRIPTIONS,
  TELEMETRY_STATUS_TO_API_STATUS,
  getAnalyticsErrorInfo,
  mapTelemetryPage,
  resolveDateRangeParams,
} from '~/lib/ai/analytics';
import { downloadCsv } from '~/lib/ai/csv';
import { formatDuration, formatMoney, formatNumber } from '~/lib/format';

import {
  ANALYTICS_FILTER_KEYS,
  AnalyticsFilterDrawer,
  DEFAULT_ANALYTICS_SORT,
  TELEMETRY_SORT_FIELDS,
  readAnalyticsScope,
} from '~/components/pages/ai/analytics/AnalyticsFilters';
import ConversationDrawer from '~/components/pages/ai/analytics/ConversationDrawer';

const Unset = () => <span className="text-gray-400 dark:text-gray-500">—</span>;

const messageLabel = (count: number) => `${count} ${count === 1 ? 'message' : 'messages'}`;

/** One row per session: who, where, how it ended, what it cost, and how long it took. */
export default function AnalyticsUserDataTab() {
  const url = useUrlParams();
  const list = useListUrlState({
    filterKeys: ANALYTICS_FILTER_KEYS,
    sortFields: TELEMETRY_SORT_FIELDS,
  });
  const scope = readAnalyticsScope(url.get);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  // The URL keeps every keystroke (a deep link can share the term); the query waits for a pause.
  const search = useDebouncedQuery(list.q.trim());
  // No sort in the URL is the server's default: newest prompt first.
  const sort = (list.sort as AnalyticsTelemetrySortField | null) ?? DEFAULT_ANALYTICS_SORT;
  const order = list.sort ? list.order! : 'desc';
  const filtered = Boolean(list.q) || scope.activeCount > 0;

  const query = {
    source: scope.source,
    entityKey: scope.entity || undefined,
    model: scope.model || undefined,
    status: scope.status ? TELEMETRY_STATUS_TO_API_STATUS[scope.status] : undefined,
    ...resolveDateRangeParams(scope.dateRange),
    sort,
    sortDirection: order,
  };

  const telemetry = useGetAnalyticsTelemetryQuery({
    ...query,
    search: search || undefined,
    page: list.page,
    pageSize: list.pageSize,
  });

  const pageData = useMemo(
    () => (telemetry.data ? mapTelemetryPage(telemetry.data) : undefined),
    [telemetry.data],
  );
  const rows = pageData?.rows ?? [];
  const totalRows = pageData?.totalCount ?? 0;
  const totalPages = Math.max(1, pageData?.totalPages ?? 1);
  const pageIndex = Math.min(list.page, totalPages) - 1;
  const errorInfo = telemetry.isError ? getAnalyticsErrorInfo(telemetry.error) : null;

  const [triggerExport, exportState] = useLazyExportAnalyticsTelemetryQuery();

  const conversation = useTransientParam('session');
  const openConversation = conversation.open;
  const closeConversation = conversation.close;

  // The export carries the applied filters and sort, minus paging: the whole filtered set.
  const handleExport = async () => {
    try {
      const result = await triggerExport({ ...query, search: list.q.trim() || undefined }).unwrap();

      downloadCsv(result.filename, result.csvContent);
      toast.success('Export downloaded');
    } catch (caught) {
      toast.error(getAnalyticsErrorInfo(caught).message);
    }
  };

  const columns: DataTableColumn<TelemetrySession>[] = [
    {
      id: 'userName',
      header: 'User',
      sortable: true,
      cell: (row) => (
        <div>
          <p className="font-medium text-gray-800 dark:text-white/90">{row.userName}</p>
          <p className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.userId}</p>
        </div>
      ),
    },
    {
      id: 'entityName',
      header: 'Placement and model',
      sortable: true,
      cell: (row) => (
        <div>
          <p>{row.entityName ?? <Unset />}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{row.model}</p>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cell: (row) => (
        <span title={TELEMETRY_STATUS_DESCRIPTIONS[row.status]}>
          <StatusChip {...telemetryStatus(row.status)} />
        </span>
      ),
    },
    {
      id: 'messageCount',
      header: 'Conversation',
      sortable: true,
      cell: (row) => (
        <Button
          variant="outline"
          onClick={() => openConversation(row.sessionId)}
          testId={`ai-analytics-conversation-open-${row.sessionId}`}
          ariaLabel={`Open the conversation for session ${row.sessionId}`}
        >
          {messageLabel(row.messageCount)}
        </Button>
      ),
    },
    {
      id: 'estimatedCostUsd',
      header: 'Tokens and cost',
      sortable: true,
      cell: (row) => (
        <div>
          <p>{formatNumber(row.totalTokens)} tokens</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatMoney(row.estimatedCostUsd)}
          </p>
        </div>
      ),
    },
    {
      id: 'durationMs',
      header: 'Duration',
      sortable: true,
      cell: (row) => formatDuration(row.durationMs),
    },
    {
      id: 'avgLatencyMs',
      header: 'Avg latency',
      sortable: true,
      cell: (row) => formatDuration(row.avgLatencyMs),
    },
    {
      id: 'promptStartAt',
      header: 'Prompt start',
      sortable: true,
      cell: (row) => <LocalTime value={row.promptStartAt} />,
    },
    {
      id: 'promptEndAt',
      header: 'Prompt end',
      sortable: true,
      cell: (row) => <LocalTime value={row.promptEndAt} />,
    },
    {
      id: 'session',
      header: 'Session',
      cell: (row) => (
        <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{row.sessionId}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <ListToolbar
          title="Telemetry sessions"
          description="One row per prompt session. Open a conversation to read its messages."
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search sessions…',
            testId: 'ai-analytics-search',
          }}
          filters={{
            count: scope.activeCount,
            onClick: () => setIsFilterOpen(true),
            onClear: list.clear,
            testId: 'ai-analytics-filters',
          }}
          actions={
            <Button
              variant="outline"
              loading={exportState.isFetching}
              startIcon={<Download className="size-4" aria-hidden="true" />}
              onClick={handleExport}
              testId="ai-analytics-export"
            >
              Export CSV
            </Button>
          }
        />
        <CardBody className="p-0">
          <DataTable<TelemetrySession>
            framed={false}
            testId="ai-analytics-telemetry-table"
            columns={columns}
            loading={telemetry.isLoading}
            refreshing={telemetry.isFetching && !!pageData}
            error={
              errorInfo && !pageData
                ? {
                    what: 'the sessions',
                    message: errorInfo.message,
                    onRetry: () => telemetry.refetch(),
                  }
                : undefined
            }
            data={rows}
            rowKey={(row) => row.sessionId}
            emptyMessage={filtered ? 'No sessions match.' : 'No sessions yet.'}
            emptyDescription={
              filtered
                ? 'Widen the date range or clear a filter to see more sessions.'
                : 'Sessions appear here once a placement or digital assistant is used.'
            }
            sort={sort}
            order={order}
            onSort={list.onSort}
          />
        </CardBody>

        <Pagination
          pageIndex={pageIndex}
          pageSize={list.pageSize}
          pageCount={totalPages}
          totalRows={totalRows}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <AnalyticsFilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        source={scope.source}
        value={list.filters}
        onApply={list.setFilters}
        onClear={list.clear}
      />

      <ConversationDrawer sessionId={conversation.value} onClose={closeConversation} />
    </div>
  );
}
